import React, { useEffect, useState, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  withTranslation,
  Stack,
  MenuSelection,
  Card,
  Text,
  StyledContainer,
  TextField,
  Spinner,
  Button,
  Layout,
} from '@piwikpro/ui-components';
import { App, ConsentManagementTool } from '@piwikpro/apps-crate';
import { withInject, errorBoundary } from '@piwikpro/platform';
import { ContainerSettings } from '@piwikpro/container-settings-crate/ContainerSettings';
import { ContainerSettingsState } from '@piwikpro/container-settings-crate/redux';
import { Notifications } from '@piwikpro/notification-crate';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { OrganizationState } from '@piwikpro/organization-crate';
import {
  ManualInstall,
  CookiebotInstall,
  WordPressInstall,
  GoogleTagManagerInstall,
  ReactInstall,
  AngularInstall,
  ShopifyInstall,
  OtherMethodsInstall,
  IOSInstall,
  AndroidInstall,
  ReactNativeInstall,
  FlutterInstall,
} from '../../InstallationMethods';

type WebsiteInstallationType = 'install-manually'
| 'wordpress'
| 'google-tag'
| 'react'
| 'angular'
| 'shopify'
| 'other-methods';

type MobileAppInstallationType = 'ios'
| 'android'
| 'react-native'
| 'flutter';

const mapStateToProps = (state: ContainerSettingsState & {
  session: {
    organizationName: SessionState['organizationName']
  }
  organization: OrganizationState
}) => {
  const { code, failed: installationCodeFailed } = state.containerSettings.app.installationCode;
  const { trackingDomain, trackingEndpointPath } = state.containerSettings.app.domainSettings;
  const {
    value: containerUrl,
    failed: containerUrlFailed,
  } = state.containerSettings.app.domainSettings.staticResourcesDomain;

  return {
    snippet: code as NonNullable<typeof code>,
    containerUrl: `https://${containerUrl}`,
    installationCodeFailed,
    containerUrlFailed,
    trackingDomain,
    trackingEndpointPath,
    organizationName: state.session.organizationName,
    license: state.organization.details.license?.current,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  containerSettings: ContainerSettings
  notifications: Notifications
  productAnalytics: ProductAnalytics
  t: TFunction
}

interface Props extends ReduxProps, InjectedProps {
  appType: 'website' | 'mobile-app'
  cmp: ConsentManagementTool | null
  app: App
  isCreatingApp: boolean
}

const WEBSITE_DEFAULT_OPTION = 'install-manually' as WebsiteInstallationType;
const MOBILE_APP_DEFAULT_OPTION = 'ios' as MobileAppInstallationType;

const isOtherMethodMatch = (searchTerm: string): boolean => {
  const otherMethods = [
    'drupal',
    'instapage',
    'squarespace',
    'wix',
    'no piwik tag manager',
    'no piwik pro tag manager',
    'tag manager',
  ];

  const searchLower = searchTerm.toLowerCase();
  return otherMethods.some(method => method.includes(searchLower) || searchLower.includes(method));
};


const InstallationStep: React.FC<Props> = ({
  t,
  containerSettings,
  notifications,
  productAnalytics,
  snippet,
  containerUrl,
  installationCodeFailed,
  containerUrlFailed,
  trackingDomain,
  trackingEndpointPath,
  organizationName,
  license,
  appType,
  cmp,
  app,
  isCreatingApp,
}) => {
  const [selectedOption, setSelectedOption] = useState<WebsiteInstallationType | MobileAppInstallationType>(appType === 'website' ? WEBSITE_DEFAULT_OPTION : MOBILE_APP_DEFAULT_OPTION);
  const [search, setSearch] = useState('');
  const onSearch = useCallback((event: { target: { value: string | number } }) => {
    setSearch(event.target.value as string);
  }, []);


  const handleMenuSelectionChange = (event: any) => {
    if (event.stopPropagation) {
      event.stopPropagation();
    }
    setSelectedOption(event.target.value as WebsiteInstallationType | MobileAppInstallationType);
  };

  const handleCopyToClipboard = (result: boolean, productAnalyticsActionType?: string) => {
    if (result) {
      notifications.push({
        text: t('components:notifications.copied.success'),
        type: 'success',
      });
      if (productAnalyticsActionType) {
        productAnalytics.push({ type: `${productAnalyticsActionType}/copy` });
      }
    } else {
      notifications.push({
        text: t('components:notifications.copied.error'),
        type: 'error',
      });
    }
  };

  const getFilteredMobileAppInstallationOptions = () => {
    const allOptions = [
      {
        id: 'ios',
        label: t('apps.creator.installation.methods.mobileApp.ios'),
        value: 'ios',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.mobileApp.ios')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <IOSInstall
                app={app}
                t={t}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'ios' as const,
        disabled: false,
      },
      {
        id: 'android',
        label: t('apps.creator.installation.methods.mobileApp.android'),
        value: 'android',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.mobileApp.android')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <AndroidInstall
                app={app}
                t={t}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'android' as const,
        disabled: false,
      },
      {
        id: 'react-native',
        label: t('apps.creator.installation.methods.mobileApp.reactNative'),
        value: 'react-native',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.mobileApp.reactNative')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <ReactNativeInstall
                app={app}
                trackingDomain={trackingDomain.value as string}
                trackingDomainFailed={trackingDomain.failed}
                t={t}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'react' as const,
        disabled: false,
      },
      {
        id: 'flutter',
        label: t('apps.creator.installation.methods.mobileApp.flutter'),
        value: 'flutter',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.mobileApp.flutter')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <FlutterInstall
                app={app}
                trackingDomain={trackingDomain.value as string}
                trackingDomainFailed={trackingDomain.failed}
                t={t}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'flutter' as const,
        disabled: false,
      },
    ];

    if (!search.trim()) {
      return allOptions;
    }

    const searchLower = search.toLowerCase();
    return allOptions.filter(option =>
      option.label.toLowerCase().includes(searchLower)
      || option.value.toLowerCase().includes(searchLower));
  };

  const mobileAppInstallationOptions = [
    {
      sectionId: 'mobile-app-methods',
      sectionItems: getFilteredMobileAppInstallationOptions(),
    },
  ];

  const getFilteredSitesInstallationOptions = () => {
    const allOptions = [
      {
        id: 'install-manually',
        label: t('apps.creator.installation.methods.site.installManually'),
        value: 'install-manually',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.installManually')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <ManualInstall
                app={app}
                t={t}
                installationCodeFailed={installationCodeFailed}
                productAnalytics={productAnalytics}
                snippet={snippet}
                cmp={cmp}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'code' as const,
        disabled: false,
      },
      {
        id: 'cookiebot',
        label: t('apps.creator.installation.methods.site.cookiebot'),
        value: 'cookiebot',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.cookiebot')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <CookiebotInstall
                app={app}
                t={t}
                installationCodeFailed={installationCodeFailed}
                productAnalytics={productAnalytics}
                snippet={snippet}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'code' as const,
        disabled: false,
      },
      {
        id: 'wordpress',
        label: t('apps.creator.installation.methods.site.wordpress'),
        value: 'wordpress',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.wordpress')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <WordPressInstall
                app={app}
                containerUrl={containerUrl}
                containerUrlFailed={containerUrlFailed}
                productAnalytics={productAnalytics}
                t={t}
                cmp={cmp}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'wordpress' as const,
        disabled: false,
      },
      {
        id: 'google-tag-manager',
        label: t('apps.creator.installation.methods.site.googleTagManager'),
        value: 'google-tag-manager',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.googleTagManager')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <GoogleTagManagerInstall
                app={app}
                t={t}
                installationCodeFailed={installationCodeFailed}
                snippet={snippet}
                cmp={cmp}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'google-tag-manager' as const,
        disabled: false,
      },
      {
        id: 'react',
        label: t('apps.creator.installation.methods.site.react'),
        value: 'react',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.react')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <ReactInstall
                app={app}
                containerUrlFailed={containerUrlFailed}
                containerUrl={containerUrl}
                t={t}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'react' as const,
        disabled: false,
      },
      {
        id: 'angular',
        label: t('apps.creator.installation.methods.site.angular'),
        value: 'angular',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.angular')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <AngularInstall
                app={app}
                containerUrl={containerUrl}
                containerUrlFailed={containerUrlFailed}
                t={t}
                handleCopyToClipboard={handleCopyToClipboard}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'angular' as const,
        disabled: false,
      },
      {
        id: 'shopify',
        label: t('apps.creator.installation.methods.site.shopify'),
        value: 'shopify',
        content: !isCreatingApp ? (
          <Stack vertical>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.shopify')}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <ShopifyInstall
                shopifyContainerSettingsAreLoading={trackingDomain.isLoading || trackingEndpointPath.isLoading}
                shopifyContainerSettingsFailed={trackingDomain.failed || trackingEndpointPath.failed}
                trackingEndpointPath={trackingEndpointPath}
                trackingDomain={trackingDomain}
                appId={app.id}
                organizationName={organizationName}
                license={license}
                t={t}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'shopify' as const,
        disabled: false,
      },
      {
        id: 'other-methods',
        label: t('apps.creator.installation.methods.site.otherMethods'),
        value: 'other-methods',
        content: !isCreatingApp ? (
          <Stack vertical fullWidth>
            <Stack.Item>
              <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                {t('apps.creator.installation.methods.site.otherMethods')}
              </Text>
            </Stack.Item>
            <Stack.Item fill>
              <OtherMethodsInstall
                productAnalytics={productAnalytics}
                t={t}
              />
            </Stack.Item>
          </Stack>
        ) : <Spinner />,
        icon: 'other-methods' as const,
        disabled: false,
      },
    ];

    if (!search.trim()) {
      return allOptions;
    }

    const searchLower = search.toLowerCase();
    return allOptions.filter((option) => {
      const standardMatch = option.label.toLowerCase().includes(searchLower)
        || option.value.toLowerCase().includes(searchLower);

      const otherMethodsMatch = option.value === 'other-methods' && isOtherMethodMatch(search);

      return standardMatch || otherMethodsMatch;
    });
  };

  const sitesInstallationOptions = [
    {
      sectionId: 'installation-methods',
      sectionItems: getFilteredSitesInstallationOptions(),
    },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        await containerSettings.getAppInstallationCode(app.id);
        await containerSettings.getAppSetting(app.id, 'static_resources_domain');
        await containerSettings.getAppSetting(app.id, 'tracking_domain');
        await containerSettings.getAppSetting(app.id, 'tracking_endpoint_path');
      } catch (err) {
        // Error handling is done by Redux state
      }
    };

    loadData();
  }, [containerSettings, app.id]);

  return (
    <Layout maxWidth="medium">
      <Stack vertical>
        <Stack.Item fill>
          <StyledContainer
            padding="narrow"
            border={['left', 'right', 'top']}
            backgroundColor="gray"
          >
            <TextField
              dataId="Installation.Search"
              name="search"
              value={search}
              onChange={onSearch}
              icon="search"
              placeholder={t('apps.creator.installation.searchPlaceholder')}
              clearable
            />
          </StyledContainer>
        </Stack.Item>
        <Stack.Item previousSpacing="none" fill>
          <Card>
            <Card.Section>
              <Stack vertical spacing="wide">
                <Stack.Item>
                  <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                    {t('apps.creator.installation.header')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow">
                  <Text as="p" size={12} dataId="Caption" color="InkLighter">{t('apps.creator.installation.caption')}</Text>
                </Stack.Item>
                <Stack.Item fill>
                  <MenuSelection
                    name="installation-type"
                    value={selectedOption}
                    items={appType === 'website' ? sitesInstallationOptions : mobileAppInstallationOptions}
                    onChange={handleMenuSelectionChange}
                    dataId="installation-selection"
                  />
                </Stack.Item>
              </Stack>
            </Card.Section>
            <Card.Section subdued padding="narrow">
              <Stack spacing="extraNarrow" verticalAlignment="center">
                <Stack.Item fill>
                  <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                    {t('apps.creator.installation.footerTitle')}
                  </Text>
                </Stack.Item>
                <Stack.Item>
                  <Button
                    text={t('apps.creator.installation.footerButton')}
                    appearance="simple"
                    dataId="AppCreatorInstallationFeedbackButton"
                  />
                </Stack.Item>
              </Stack>
            </Card.Section>
          </Card>
        </Stack.Item>
      </Stack>
    </Layout>
  );
};

export default connector(withInject<InjectedProps>({
  containerSettings: 'ContainerSettingsCrate.ContainerSettings',
  notifications: 'NotificationCrate.notifications',
  productAnalytics: 'productAnalytics',
})(
  withTranslation('administration')(errorBoundary(InstallationStep)),
));
